import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import "../styles/admin.css";

const apiBase = "/admin";
const formBase = "/festivalaki";

// Human-friendly display labels for common fields. Keys are normalized when looked up.
const FIELD_LABELS = {
	created: "Created",
	email: "Email",
	name: "Name",
	classes: "Classes",
	role_open: "Role (O)",
	role_advanced: "Role (A)",
	role_invitational: "Role (A+)",
	tshirt_konto: "Konto",
	tshirt_konto_f: "Konto F",
	tote_diy26: "Tote (26)",
	tote_diy25: "Tote (25)",
	computedtotal: "Total",
	total: "Total",
	paid: "Paid",
	reg: "REG",
	actions: "Actions"
};

const normalizeKey = (s) =>
	String(s || "")
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "_");

function getLabel(key) {
	const k = normalizeKey(key);
	return FIELD_LABELS[k] || String(key);
}

function getAnswerValue(answers, fieldName) {
	const key = Object.keys(answers || {}).find((name) => name.toLowerCase() === fieldName.toLowerCase());
	return key ? answers[key] : undefined;
}

function Table({
	title,
	className,
	data,
	onEdit,
	onDelete,
	onTogglePaid,
	onToggleReg,
	editing,
	setEditing,
	setConfirmState,
	confirmEdit,
	schemaMap,
	orderMap,
	filtering,
	onEditNote
}) {
	if (!data) return null;
	if (data.length === 0)
		return (
			<div className={`registrations-table ${className || ""}`}>
				<h3 className="registrations-table-heading">{title}</h3>
				<p className="rs-note">{filtering ? "No matches" : "No submissions yet"}</p>
			</div>
		);
	const formKeyForTable = data[0].formKey;
	const desiredOrder = (orderMap?.[formKeyForTable] || []).filter((f) => f !== "payCashConsent");
	// Collect actual keys present in data (exclude internal DB fields we don't want as columns)
	let present = Array.from(
		data.reduce((set, row) => {
			Object.keys(row.answers || {}).forEach((k) => set.add(k));
			return set;
		}, new Set())
	);
	// never show computedTotal or temporary UI keys as answers columns — TOTAL is rendered separately
	// also exclude accidental keys that would duplicate the index column ("#" / "index") and any reg variants
	const _normKey = (s) =>
		String(s || "")
			.toLowerCase()
			.replace(/[^a-z0-9]/g, "");
	present = present.filter((k) => {
		if (k === "#") return false;
		const nk = _normKey(k);
		return ![
			"computedtotal",
			"editedtotal",
			"tshirtdiy",
			"totediy",
			"reg",
			"index",
			"answers",
			"emailreceipt"
		].includes(nk);
	});
	// Keep role columns visible even when every current submission left them blank,
	// so those roles can be added later from the admin editor.
	["role_Open", "role_Advanced", "role_Invitational"].forEach((roleKey) => {
		if (desiredOrder.includes(roleKey) && !present.some((key) => _normKey(key) === _normKey(roleKey))) {
			present.push(roleKey);
		}
	});
	// Build ordered columns: schema order first, then any extras not in schema (legacy fields) appended in stable order
	const schemaOrdered = desiredOrder.filter((k) => present.includes(k));
	const extras = present.filter((k) => !desiredOrder.includes(k) && k !== "payCashConsent");
	const cols = [...schemaOrdered, ...extras];

	const formatDisplayedValue = (row, fieldName, value) => {
		if (row.formKey === "registration-26" && fieldName === "pass" && typeof value === "string") {
			const match = value.match(/\(([^()]*)\)/);
			return match ? match[1] : value;
		}
		if (row.formKey === "registration-26-css" && fieldName === "classes" && typeof value === "string") {
			return value
				.split(",")
				.map((option) => {
					const match = option.trim().match(/^(Lindy Hop|Authentic Jazz)\b.*?\(([^()]*)\)\s*$/i);
					if (!match) return option.trim();
					const prefix = /^Lindy Hop$/i.test(match[1]) ? "LH" : "SJ";
					return `${prefix} (${match[2]})`;
				})
				.join(", ");
		}
		if (["tote_diy26", "tote_diy25"].includes(fieldName) && value === "No tote bag") {
			return "No";
		}
		return value;
	};

	// Helper to resolve value for a given row and column key. Attempts direct lookup first,
	// then normalized-key lookup so variants like role_Open / role_open / roleOpen map correctly.
	const resolveAnswerValue = (row, key) => {
		const answers = row.answers || {};
		if (Object.prototype.hasOwnProperty.call(answers, key)) return answers[key];
		const normalize = (s) =>
			String(s || "")
				.toLowerCase()
				.replace(/[^a-z0-9]/g, "");
		const target = normalize(key);
		for (const ak of Object.keys(answers)) {
			if (normalize(ak) === target) return answers[ak];
		}
		return undefined;
	};

	const renderEditor = (row, fieldName) => {
		// special UI-only editable total field (renders inside the visible TOTAL column)
		if (fieldName === "__editedTotal") {
			const value = editing?.draft?.__editedTotal;
			return (
				<input
					type="number"
					step="0.01"
					className="price-edit-input"
					value={value === null || value === undefined ? "" : String(value)}
					onChange={(e) =>
						setEditing({ ...editing, draft: { ...editing.draft, __editedTotal: e.target.value } })
					}
				/>
			);
		}

		// Try to find the field definition first on the row's schema, then fall back to any loaded schema.
		let fieldDef = schemaMap[row.formKey]?.[fieldName];
		if (!fieldDef) {
			for (const k of Object.keys(schemaMap || {})) {
				if (k === "__order") continue;
				const map = schemaMap[k];
				const fieldKey = Object.keys(map || {}).find((key) => key.toLowerCase() === fieldName.toLowerCase());
				if (fieldKey) {
					fieldDef = map[fieldKey];
					break;
				}
			}
		}
		const value = editing.draft[fieldName];
		if (!fieldDef) {
			return (
				<input
					className="rs-input"
					value={value ?? ""}
					onChange={(e) =>
						setEditing({ ...editing, draft: { ...editing.draft, [fieldName]: e.target.value } })
					}
				/>
			);
		}
		switch (fieldDef.type) {
			case "singleSelect":
				return (
					<select
						className="edit-select-input"
						value={value === "--" ? "" : value || ""}
						onChange={(e) =>
							setEditing({ ...editing, draft: { ...editing.draft, [fieldName]: e.target.value } })
						}
					>
						<option value="">--</option>
						{(fieldDef.options || []).map((o) => (
							<option key={o} value={o}>
								{o}
							</option>
						))}
					</select>
				);
			case "multiSelect": {
				const current = Array.isArray(value) ? value : [];
				return (
					<div className="rs-checkbox-list">
						{(fieldDef.options || []).map((opt) => {
							const checked = current.includes(opt);
							return (
								<label key={opt} className="rs-checkbox-label">
									<input
										type="checkbox"
										checked={checked}
										onChange={() => {
											const set = new Set(current);
											if (set.has(opt)) set.delete(opt);
											else set.add(opt);
											setEditing({
												...editing,
												draft: { ...editing.draft, [fieldName]: Array.from(set) }
											});
										}}
									/>
									<span>{opt}</span>
								</label>
							);
						})}
					</div>
				);
			}
			case "checkbox":
				return (
					<label className="rs-checkbox-single">
						<input
							type="checkbox"
							checked={!!value}
							onChange={(e) =>
								setEditing({
									...editing,
									draft: { ...editing.draft, [fieldName]: e.target.checked }
								})
							}
						/>
						<span>Yes</span>
					</label>
				);
			case "longText":
				return (
					<textarea
						rows={3}
						className="rs-textarea"
						value={value || ""}
						onChange={(e) =>
							setEditing({ ...editing, draft: { ...editing.draft, [fieldName]: e.target.value } })
						}
					/>
				);
			case "email":
				return (
					<input
						type="email"
						className="edit-email-input"
						value={value || ""}
						onChange={(e) =>
							setEditing({ ...editing, draft: { ...editing.draft, [fieldName]: e.target.value } })
						}
					/>
				);
			case "shortText":
			default:
				return (
					<input
						className="name-edit-input"
						value={value ?? ""}
						onChange={(e) =>
							setEditing({ ...editing, draft: { ...editing.draft, [fieldName]: e.target.value } })
						}
					/>
				);
		}
	};

	return (
		<div className={`registrations-table ${className || ""}`}>
			<h3 className="registrations-table-heading">{title}</h3>
			<div className="registrations-content-wrapper">
				<table>
					<thead>
						<tr>
							<th></th>
							<th>#</th>
							<th>{getLabel("created")}</th>
							{cols.map((c) => (
								<th key={c}>{getLabel(c)}</th>
							))}
							<th>{getLabel("total")}</th>
							<th>{getLabel("reg")}</th>
							<th>{getLabel("paid")}</th>
							<th>{getLabel("actions")}</th>
						</tr>
					</thead>
					<tbody>
						{data.map((row, i) => {
							const isEditing = editing && editing._id === row._id;
							return (
								<tr key={row._id} className={isEditing ? "panel-body-editing" : undefined}>
									<td className="admin-note-cell">
										<button onClick={() => onEditNote(row)}>
											<img src={row.adminNote ? "/note_full.png" : "/note_empty.png"} />
										</button>
										</td>
									<td>{i + 1}</td>
									<td>{new Date(row.createdAt).toLocaleString()}</td>
									{cols.map((c) => {
										const rawVal = resolveAnswerValue(row, c);
										let v = rawVal;
										if (Array.isArray(v)) v = v.join(", ");
										v = formatDisplayedValue(row, c, v);
										if (isEditing) {
											return <td key={c}>{renderEditor(row, c)}</td>;
										}
										return (
											<td key={c}>
												{v === undefined || v === null || v === "" ? (
													<span className="rs-empty-value">--</span>
												) : (
													String(v)
												)}
											</td>
										);
									})}
									<td>
										{isEditing
											? // Use our renderEditor path for numeric editing of visible TOTAL
											  renderEditor(row, "__editedTotal")
											: row.computedTotal != null
											? row.computedTotal
											: "—"}
									</td>
									<td>
										<label className="reg-edit-checkbox">
											<input
												type="checkbox"
												checked={!!(row.reg ?? resolveAnswerValue(row, "reg"))}
												onChange={(e) => onToggleReg(row, e.target.checked)}
											/>
										</label>
									</td>
									<td>
										<label className="paid-edit-checkbox">
											<input
												type="checkbox"
												checked={!!row.paid}
												onChange={(e) => onTogglePaid(row, e.target.checked)}
											/>
										</label>
									</td>
									<td>
										{isEditing ? (
											<div className="registrations-table-actions">
												<button onClick={() => confirmEdit()} className="save-button">
													Save
												</button>
												<button
													onClick={() => {
														setEditing(null);
														setConfirmState(null);
													}}
													className="cancel-button"
												>
													Cancel
												</button>
											</div>
										) : (
											<div className="registrations-table-actions">
												<button
													onClick={() => onEdit(row, cols, schemaMap)}
													className="edit-button"
												>
													Edit
												</button>
												<button onClick={() => onDelete(row)} className="delete-button">
													Delete
												</button>
											</div>
										)}
									</td>
								</tr>
							);
						})}
						{data.length === 0 && (
							<tr>
								<td className="rs-empty" colSpan={cols.length + 6}>
									No submissions yet
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}
// ...existing code...

export default function Registrations() {
	const { token } = useAuth();
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [editing, setEditing] = useState(null); // { _id, formKey, draft, original }
	const [confirmState, setConfirmState] = useState(null); // { type, payload }
	const [noteEditing, setNoteEditing] = useState(null); // { row, text }
	const [confirmDeleteNote, setConfirmDeleteNote] = useState(false);
	const [showAudit, setShowAudit] = useState(false);
	const [audit, setAudit] = useState({ loading: false, items: [], error: null });
	const [schemaMap, setSchemaMap] = useState({}); // formKey -> fieldName -> fieldDef

	// Custom column order for CSS form (requested by admin)
	const CSS_COLUMN_ORDER = useMemo(
		() => [
			"created",
			"email",
			"name",
			"classes",
			"role_Open",
			"role_Advanced",
			"role_Invitational",
			"tshirt_konto",
			"tshirt_konto_f",
			"tote_diy26",
			"tote_diy25",
			"emailReceipt",
			"computedTotal",
			"paid"
		],
		[]
	);
	const CUSTOM_ORDER_MAP = useMemo(
		() => ({ ...(schemaMap.__order || {}), "registration-26-css": CSS_COLUMN_ORDER }),
		[schemaMap, CSS_COLUMN_ORDER]
	);
	const [updatingPaid, setUpdatingPaid] = useState({}); // id -> loading flag
	const [updatingReg, setUpdatingReg] = useState({}); // id -> loading flag for REG
	const [search, setSearch] = useState(""); // name search

	async function loadAll() {
		if (!token) return;
		setLoading(true);
		setError(null);
		try {
			const [subRes, schemaNormalRes, schemaCssRes] = await Promise.all([
				fetch(`${apiBase}/submissions`, { headers: { Authorization: `Bearer ${token}` } }),
				fetch(`${formBase}/registration-26`),
				fetch(`${formBase}/registration-26-css`)
			]);
			const [subJson, schemaNormal, schemaCss] = await Promise.all([
				subRes.json(),
				schemaNormalRes.json(),
				schemaCssRes.json()
			]);
			if (!subRes.ok) throw new Error(subJson.error || "Failed to load submissions");
			setData(subJson);
			const buildMap = (schema) => {
				const m = {};
				(schema.sections || []).forEach((sec) =>
					(sec.fields || []).forEach((f) => {
						m[f.name] = f;
					})
				);
				return m;
			};
			const buildOrder = (schema) => {
				const order = [];
				(schema.sections || []).forEach((sec) =>
					(sec.fields || []).forEach((f) => {
						order.push(f.name);
					})
				);
				(schema.standaloneFields || []).forEach((f) => order.push(f.name));
				return order;
			};
			setSchemaMap({
				[schemaNormal.key]: buildMap(schemaNormal),
				[schemaCss.key]: buildMap(schemaCss),
				__order: {
					[schemaNormal.key]: buildOrder(schemaNormal),
					[schemaCss.key]: buildOrder(schemaCss)
				}
			});
		} catch (e) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		loadAll();
	}, [token]);

	function formatAuditDate(iso) {
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return String(iso);
		const pad = (n) => String(n).padStart(2, "0");
		const day = pad(d.getDate());
		const month = pad(d.getMonth() + 1);
		const year = d.getFullYear();
		let hours = d.getHours();
		const minutes = pad(d.getMinutes());
		const seconds = pad(d.getSeconds());
		const ampm = hours >= 12 ? "PM" : "AM";
		hours = hours % 12;
		if (hours === 0) hours = 12;
		return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
	}

	// Filtered datasets by name (case-insensitive substring match on answers.name)
	const filteredNormal = useMemo(() => {
		if (!data?.normal) return [];
		const q = search.trim().toLowerCase();
		if (!q) return data.normal;
		return data.normal.filter((row) => (row.answers?.name || "").toLowerCase().includes(q));
	}, [data, search]);
	const filteredCss = useMemo(() => {
		if (!data?.css) return [];
		const q = search.trim().toLowerCase();
		if (!q) return data.css;
		return data.css.filter((row) => (row.answers?.name || "").toLowerCase().includes(q));
	}, [data, search]);

	// Helper: build stable ordered columns for a dataset (same logic as Table)
	function buildColsForData(dataset) {
		if (!dataset || dataset.length === 0) return [];
		let present = Array.from(
			dataset.reduce((set, row) => {
				Object.keys(row.answers || {}).forEach((k) => set.add(k));
				return set;
			}, new Set())
		);
		const _normKey = (s) =>
			String(s || "")
				.toLowerCase()
				.replace(/[^a-z0-9]/g, "");
		present = present.filter((k) => {
			if (k === "#") return false;
			const nk = _normKey(k);
			return ![
				"computedtotal",
				"editedtotal",
				"tshirtdiy",
				"totediy",
				"reg",
				"index",
				"answers"
			].includes(nk);
		});
		const formKeyForTable = dataset[0].formKey;
		const rawDesired = (
			CUSTOM_ORDER_MAP?.[formKeyForTable] ||
			schemaMap.__order?.[formKeyForTable] ||
			[]
		)
			.filter((f) => f !== "payCashConsent")
			.filter((f) => {
				// these are rendered outside of the answers columns
				const lower = String(f || "").toLowerCase();
				return (
					lower !== "created" && lower !== "computedtotal" && lower !== "paid" && lower !== "total"
				);
			});

		const normalize = (s) =>
			String(s || "")
				.toLowerCase()
				.replace(/[^a-z0-9]/g, "");

		// build lookup of present normalized -> actual key
		const presentMap = {};
		present.forEach((p) => {
			presentMap[normalize(p)] = p;
		});

		const schemaOrdered = [];
		const used = new Set();

		// Always include desired fields in the given order. If a matching present key exists
		// use that actual key for lookups; otherwise include the desired name as a placeholder
		// so the column header still appears (will be empty for all rows).
		rawDesired.forEach((d) => {
			const nd = normalize(d);
			if (presentMap[nd]) {
				schemaOrdered.push(presentMap[nd]);
				used.add(presentMap[nd]);
			} else {
				// include the desired string as-is so header shows; no present value will be found
				schemaOrdered.push(d);
			}
		});

		// append any remaining present keys (not already used) in stable alphabetical order
		const extras = present.filter((k) => !used.has(k)).sort((a, b) => a.localeCompare(b));
		return [...schemaOrdered, ...extras];
	}

	// Helper to resolve value for a given row and column key. Attempts direct lookup first,
	// then normalized-key lookup so variants like role_Open / role_open / roleOpen map correctly.
	function resolveAnswerValue(row, key) {
		const answers = row.answers || {};
		if (Object.prototype.hasOwnProperty.call(answers, key)) return answers[key];
		const normalize = (s) =>
			String(s || "")
				.toLowerCase()
				.replace(/[^a-z0-9]/g, "");
		const target = normalize(key);
		for (const ak of Object.keys(answers)) {
			if (normalize(ak) === target) return answers[ak];
		}
		return undefined;
	}

	function csvEscape(val) {
		if (val === null || val === undefined) return "";
		const s = String(val);
		if (s.includes("\n") || s.includes(",") || s.includes('"')) {
			return '"' + s.replace(/"/g, '""') + '"';
		}
		return s;
	}

	function downloadCsv(filename, rows) {
		const csv = rows.map((r) => r.join(",")).join("\n");
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	function exportDatasetAsCsv(dataset, label) {
		if (!dataset || dataset.length === 0) return;
		const cols = buildColsForData(dataset);
		const headers = ["#", "created", ...cols, "reg", "total", "paid"];
		const rows = [headers.map(csvEscape)];
		dataset.forEach((row, i) => {
			const base = [String(i + 1), new Date(row.createdAt).toLocaleString()];
			const answers = cols.map((c) => {
				let v = row.answers?.[c];
				if (Array.isArray(v)) v = v.join(" | ");
				return csvEscape(v === undefined || v === null || v === "" ? "" : v);
			});
			const reg = csvEscape(row.reg ? "Yes" : "No");
			const total = csvEscape(row.computedTotal != null ? row.computedTotal : "");
			const paid = csvEscape(row.paid ? "Yes" : "No");
			rows.push([...base.map(csvEscape), ...answers, reg, total, paid]);
		});
		const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
		const filename = `${label}-registrations-${ts}.csv`;
		downloadCsv(filename, rows);
	}

	// CSS form pricing helper: derive total from count of individual classes in comma-separated string
	function computeCssTotalFromClasses(classesValue) {
		if (!classesValue) return null;
		let raw = classesValue;
		if (Array.isArray(raw)) {
			// defensive: join if stored as array
			raw = raw.join(",");
		}
		if (typeof raw !== "string") return null;
		// split on comma and count non-empty parts
		const count = raw
			.split(",")
			.map((s) => s.trim())
			.filter((s) => s.length > 0).length;
		if (count <= 0) return null;
		// pricing grid from screenshot
		switch (count) {
			case 1:
				return 12;
			case 2:
				return 22;
			case 3:
				return 30;
			case 4:
				return 38;
			case 5:
				return 44;
			default:
				return 50;
		}
	}

	// Recalculate aggregates locally (mirrors backend logic)
	function recalcAggregates(normal, css) {
		const totalNormal = (normal || []).reduce((sum, r) => sum + (r.computedTotal || 0), 0);
		const totalCss = (css || []).reduce((sum, r) => sum + (r.computedTotal || 0), 0);
		const paidNormal = (normal || [])
			.filter((r) => r.paid)
			.reduce((s, r) => s + (r.computedTotal || 0), 0);
		const paidCss = (css || [])
			.filter((r) => r.paid)
			.reduce((s, r) => s + (r.computedTotal || 0), 0);
		return {
			totalNormal,
			totalCss,
			grandTotal: totalNormal + totalCss,
			paidNormal,
			paidCss,
			paidGrand: paidNormal + paidCss,
			owedNormal: totalNormal - paidNormal,
			owedCss: totalCss - paidCss,
			owedGrand: totalNormal + totalCss - (paidNormal + paidCss)
		};
	}

	function mergeAnswerChanges(answers, changes) {
		const nextAnswers = { ...(answers || {}) };
		const roleKeys = ["role_Open", "role_Advanced", "role_Invitational"];
		Object.entries(changes).forEach(([rawKey, value]) => {
			if (rawKey === "paid" || rawKey === "computedTotal") return;
			const key = roleKeys.find((roleKey) => roleKey.toLowerCase() === rawKey.toLowerCase()) || rawKey;
			Object.keys(nextAnswers).forEach((answerKey) => {
				if (answerKey.toLowerCase() === key.toLowerCase() && answerKey !== key) delete nextAnswers[answerKey];
			});
			nextAnswers[key] = value;
		});
		return nextAnswers;
	}

	function onEdit(row, cols) {
		const formFieldMap = schemaMap[row.formKey] || {};
		const draft = {};
		cols.forEach((c) => {
			// resolve field def from the row's schema or any loaded schema (fallback for CSS fields)
			let def = formFieldMap[c];
			if (!def) {
				for (const k of Object.keys(schemaMap || {})) {
					if (k === "__order") continue;
					const map = schemaMap[k];
					const fieldKey = Object.keys(map || {}).find((key) => key.toLowerCase() === c.toLowerCase());
					if (fieldKey) {
						def = map[fieldKey];
						break;
					}
				}
			}
			const originalKey = Object.keys(row.answers || {}).find((key) => key.toLowerCase() === c.toLowerCase());
			const originalVal = originalKey ? row.answers[originalKey] : undefined;
			if (def?.type === "multiSelect") {
				draft[c] = Array.isArray(originalVal)
					? originalVal.slice()
					: typeof originalVal === "string"
					? originalVal
							.split(",")
							.map((value) => value.trim())
							.filter(Boolean)
					: [];
			} else if (def?.type === "checkbox") {
				draft[c] = !!originalVal;
			} else {
				draft[c] = originalVal ?? "";
			}
		});
		// include a UI-only edited total so the visible TOTAL cell can be edited
		draft.__editedTotal = row.computedTotal ?? "";
		// keep original computedTotal separately so we can diff it later
		const originalComputed = row.computedTotal;
		setEditing({
			_id: row._id,
			formKey: row.formKey,
			draft,
			original: row.answers,
			originalComputed
		});
	}

	function onDelete(row) {
		setConfirmState({ type: "delete", payload: row });
	}

	function onEditNote(row) {
		setConfirmDeleteNote(false);
		setNoteEditing({
			row,
			text: row.adminNote || ""
		});
	}

	async function saveNote() {
		if (!noteEditing) return;
		const { row, text } = noteEditing;
		try {
			const res = await fetch(`${apiBase}/submissions/${row._id}`, {
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify({ confirm: true, changes: { adminNote: text } })
			});
			if (!res.ok) throw new Error("Failed to save note");

			setData((prev) => {
				if (!prev) return prev;
				const key = row.formKey === "registration-26" ? "normal" : "css";
				const updatedList = prev[key].map((r) =>
					r._id === row._id ? { ...r, adminNote: text } : r
				);
				return {
					...prev,
					[key]: updatedList
				};
			});
			setNoteEditing(null);
			setConfirmDeleteNote(false);
			if (showAudit) loadAudit();
		} catch (e) {
			alert(e.message || "Failed to save note");
		}
	}

	async function deleteNote() {
		if (!noteEditing) return;
		const { row } = noteEditing;
		try {
			const res = await fetch(`${apiBase}/submissions/${row._id}`, {
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify({ confirm: true, changes: { adminNote: "" } })
			});
			if (!res.ok) throw new Error("Failed to delete note");

			setData((prev) => {
				if (!prev) return prev;
				const key = row.formKey === "registration-26" ? "normal" : "css";
				const updatedList = prev[key].map((r) =>
					r._id === row._id ? { ...r, adminNote: "" } : r
				);
				return {
					...prev,
					[key]: updatedList
				};
			});
			setNoteEditing(null);
			setConfirmDeleteNote(false);
			if (showAudit) loadAudit();
		} catch (e) {
			alert(e.message || "Failed to delete note");
		}
	}

	async function onTogglePaid(row, value) {
		if (updatingPaid[row._id]) return;
		setUpdatingPaid((m) => ({ ...m, [row._id]: true }));
		const prevPaid = row.paid;
		// Optimistic UI update: update row + aggregates immediately
		setData((prev) => {
			if (!prev) return prev;
			const key = row.formKey === "registration-26" ? "normal" : "css";
			const updatedList = prev[key].map((r) => (r._id === row._id ? { ...r, paid: value } : r));
			const next = {
				...prev,
				[key]: updatedList,
				aggregates: recalcAggregates(
					key === "normal" ? updatedList : prev.normal,
					key === "css" ? updatedList : prev.css
				)
			};
			return next;
		});
		try {
			const res = await fetch(`${apiBase}/submissions/${row._id}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ confirm: true, changes: { paid: value } })
			});
			if (!res.ok) throw new Error("Failed to update paid status");
		} catch (e) {
			alert(e.message || "Failed to toggle paid status");
			// Revert optimistic change on failure
			setData((prev) => {
				if (!prev) return prev;
				const key = row.formKey === "registration-26" ? "normal" : "css";
				const updatedList = prev[key].map((r) =>
					r._id === row._id ? { ...r, paid: prevPaid } : r
				);
				return {
					...prev,
					[key]: updatedList,
					aggregates: recalcAggregates(
						key === "normal" ? updatedList : prev.normal,
						key === "css" ? updatedList : prev.css
					)
				};
			});
		} finally {
			setUpdatingPaid((m) => ({ ...m, [row._id]: false }));
		}
	}

	async function onToggleReg(row, value) {
		if (updatingReg[row._id]) return;
		setUpdatingReg((m) => ({ ...m, [row._id]: true }));
		const prevReg = row.reg;
		// Optimistic UI update: only flip the reg flag (no pricing or aggregates affected)
		setData((prev) => {
			if (!prev) return prev;
			const key = row.formKey === "registration-26" ? "normal" : "css";
			const updatedList = prev[key].map((r) =>
				r._id === row._id ? { ...r, reg: value, answers: { ...(r.answers || {}), reg: value } } : r
			);
			return {
				...prev,
				[key]: updatedList
			};
		});
		try {
			const res = await fetch(`${apiBase}/submissions/${row._id}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({
					confirm: true,
					changes: { reg: value, answers: { ...(row.answers || {}), reg: value } }
				})
			});
			if (!res.ok) throw new Error("Failed to update REG status");
		} catch (e) {
			alert(e.message || "Failed to toggle REG status");
			// Revert optimistic change on failure
			setData((prev) => {
				if (!prev) return prev;
				const key = row.formKey === "registration-26" ? "normal" : "css";
				const updatedList = prev[key].map((r) =>
					r._id === row._id
						? { ...r, reg: prevReg, answers: { ...(r.answers || {}), reg: prevReg } }
						: r
				);
				return {
					...prev,
					[key]: updatedList
				};
			});
		} finally {
			setUpdatingReg((m) => ({ ...m, [row._id]: false }));
		}
	}

	function confirmEdit() {
		if (!editing) return;
		// Validate against schema (required + option integrity)
		for (const [fname, draftVal] of Object.entries(editing.draft)) {
			// resolve field def from the editing form's schema or any loaded schema as a fallback
			let def = schemaMap[editing.formKey] && schemaMap[editing.formKey][fname];
			if (!def) {
				for (const k of Object.keys(schemaMap || {})) {
					if (k === "__order") continue;
					const map = schemaMap[k];
					if (map && map[fname]) {
						def = map[fname];
						break;
					}
				}
			}
			if (!def) continue;
			if (def.required) {
				if (def.type === "multiSelect" && (!Array.isArray(draftVal) || draftVal.length === 0)) {
					alert(`Field "${fname}" is required and must have at least one selection`);
					return;
				}
				if (def.type === "singleSelect" && (!draftVal || String(draftVal).trim() === "")) {
					alert(`Field "${fname}" is required`);
					return;
				}
				if (def.type === "checkbox" && !draftVal) {
					alert(`Required consent/checkbox "${fname}" must be checked`);
					return;
				}
				if (
					(def.type === "shortText" || def.type === "longText" || def.type === "email") &&
					(!draftVal || String(draftVal).trim() === "")
				) {
					alert(`Field "${fname}" is required`);
					return;
				}
			}
			// If it's a singleSelect / multiSelect ensure value(s) in options
			if (def.type === "singleSelect") {
				// Allow clearing non-required singleSelect (especially role_* fields) -> will store as '--'
				const isRoleField = /^role_/i.test(fname);
				if (draftVal && draftVal !== "--" && !def.options.includes(draftVal)) {
					// For role_* fields, "--" is a special cleared value that we accept
					// All other non-empty values must be one of the defined options
					if (!isRoleField || draftVal !== "--") {
						alert(`Invalid value for "${fname}"`);
						return;
					}
				}
			}
			if (def.type === "multiSelect" && Array.isArray(draftVal)) {
				const invalid = draftVal.filter((v) => !def.options.includes(v));
				if (invalid.length) {
					alert(`Invalid selection(s) for "${fname}": ${invalid.join(", ")}`);
					return;
				}
			}
		}
		const changes = {};
		Object.keys(editing.draft).forEach((k) => {
			const newVal = editing.draft[k];
			const originalKey = Object.keys(editing.original || {}).find((key) => key.toLowerCase() === k.toLowerCase());
			const prev = originalKey ? editing.original[originalKey] : undefined;
			const diff = Array.isArray(newVal)
				? JSON.stringify(newVal) !== JSON.stringify(prev)
				: newVal !== prev;
			if (diff) {
				if (/^role_/i.test(k)) {
					const roleKey = ["role_Open", "role_Advanced", "role_Invitational"].find(
						(role) => role.toLowerCase() === k.toLowerCase()
					);
					changes[roleKey || k] = newVal === "" || newVal === null ? "--" : newVal;
				} else changes[k] = newVal;
			}
		});
		if (editing.formKey === "registration-26-css") {
			const classes = (Array.isArray(editing.draft.classes)
				? editing.draft.classes
				: String(editing.draft.classes || "").split(","))
				.map((className) => String(className).trim())
				.filter(Boolean);
			const originalClasses = (Array.isArray(editing.original?.classes)
				? editing.original.classes
				: String(editing.original?.classes || "").split(","))
				.map((className) => String(className).trim())
				.filter(Boolean);
			const classLevels = {
				Open: /Lindy Hop\s+Footprints/i,
				Advanced: /Lindy Hop\s+Milestones/i,
				Invitational: /Lindy Hop\s+Giant Steps/i
			};
			for (const [level, classPattern] of Object.entries(classLevels)) {
				const roleKey = `role_${level}`;
				const roleChanged = changes[roleKey] !== undefined;
				const classesChanged = JSON.stringify(classes) !== JSON.stringify(originalClasses);
				if (!roleChanged && !classesChanged) continue;
				const hasClass = classes.some((className) => classPattern.test(className));
				const roleValue = String(editing.draft[roleKey] || "").trim().toLowerCase();
				const hasRole = roleValue === "leader" || roleValue === "follower";
				if (hasRole !== hasClass) {
					alert(`CSS ${level} role and Lindy Hop class must be selected together`);
					return;
				}
			}
		}
		// Handle totals per form type
		if (editing.formKey === "registration-26") {
			// Normal form: allow manual override of total via __editedTotal
			if (typeof editing.draft.__editedTotal !== "undefined") {
				const draftTotal = editing.draft.__editedTotal;
				const originalTotal = editing.originalComputed;
				const parsedDraft = draftTotal === "" || draftTotal === null ? null : Number(draftTotal);
				if (parsedDraft !== originalTotal) {
					changes.computedTotal = parsedDraft; // server expects computedTotal at document root
				}
			}
		} else if (editing.formKey === "registration-26-css") {
			// CSS form: total is derived from number of classes + merch (t-shirt / tote)
			const classesValue =
				editing.draft.classes !== undefined ? editing.draft.classes : editing.original.classes;
			let cssTotal = computeCssTotalFromClasses(classesValue);
			// Include merch based on current draft/original answers
			// tshirt_konto (15 EUR) when not "No T-Shirt"
			const tshirtVal =
				editing.draft.tshirt_konto !== undefined
					? editing.draft.tshirt_konto
					: editing.original.tshirt_konto;
			if (tshirtVal && tshirtVal !== "No T-Shirt") {
				cssTotal += 15;
			}
			// tshirt_konto_f (15 EUR) when not "No T-Shirt"
			const tshirtFVal =
				editing.draft.tshirt_konto_f !== undefined
					? editing.draft.tshirt_konto_f
					: editing.original.tshirt_konto_f;
			if (tshirtFVal && tshirtFVal !== "No T-Shirt") {
				cssTotal += 15;
			}
			// tote_diy26 (8 EUR) when "Yes"
			const toteDiy26Val =
				editing.draft.tote_diy26 !== undefined
					? editing.draft.tote_diy26
					: editing.original.tote_diy26;
			if (toteDiy26Val === "Yes") {
				cssTotal += 8;
			}
			// tote_diy25 (5 EUR) when "Yes"
			const toteDiy25Val =
				editing.draft.tote_diy25 !== undefined
					? editing.draft.tote_diy25
					: editing.original.tote_diy25;
			if (toteDiy25Val === "Yes") {
				cssTotal += 5;
			}
			if (cssTotal !== editing.originalComputed) {
				changes.computedTotal = cssTotal;
			}
		}
		// Make sure the temporary UI key never gets persisted
		if ("__editedTotal" in changes) delete changes.__editedTotal;
		if (Object.keys(changes).length === 0) {
			setEditing(null);
			return;
		}
		setConfirmState({
			type: "edit",
			payload: { id: editing._id, formKey: editing.formKey, changes }
		});
	}

	async function performConfirmed() {
		if (!confirmState) return;
		try {
			if (confirmState.type === "edit") {
				const { id, changes, formKey } = confirmState.payload;
				const res = await fetch(`${apiBase}/submissions/${id}`, {
					method: "PATCH",
					headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
					body: JSON.stringify({ confirm: true, changes })
				});
				if (!res.ok) throw new Error("Edit failed");
				// If pricing relevant fields changed, refetch row or recompute client-side? Simpler: refetch all.
				// If any pricing-relevant field changed, refetch submissions so computed totals are up-to-date
				const PRICING_KEYS = [
					"pass",
					"classes",
					"tshirt_konto",
					"tshirt_konto_f",
					"tote_konto",
					"tote_diy26",
					"tote_diy25"
				]; // DIY merch added
				// Only treat pricing changes as requiring recompute for the normal registration form
				const isNormalForm = formKey === "registration-26";
				const pricingChanged = isNormalForm && PRICING_KEYS.some((f) => f in changes);
				if (pricingChanged) {
					// Lightweight refresh just submissions
					try {
						const subRes = await fetch(`${apiBase}/submissions`, {
							headers: { Authorization: `Bearer ${token}` }
						});
						const subJson = await subRes.json();
						if (subRes.ok) setData(subJson);
					} catch (e) {
						// fallback to local optimistic update without new total
						setData((prev) => {
							if (!prev) return prev;
							const key = formKey === "registration-26" ? "normal" : "css";
							const updated = prev[key].map((r) =>
								r._id === id ? { ...r, answers: mergeAnswerChanges(r.answers, changes) } : r
							);
							const aggregates = recalcAggregates(
								key === "normal" ? updated : prev.normal,
								key === "css" ? updated : prev.css
							);
							return { ...prev, [key]: updated, aggregates };
						});
					}
				} else {
					setData((prev) => {
						if (!prev) return prev;
						const key = formKey === "registration-26" ? "normal" : "css";
						const updated = prev[key].map((r) =>
							r._id === id
								? {
										...r,
										answers: mergeAnswerChanges(r.answers, changes),
										computedTotal:
											typeof changes.computedTotal !== "undefined"
												? changes.computedTotal
												: r.computedTotal
								  }
								: r
						);
						const aggregates = recalcAggregates(
							key === "normal" ? updated : prev.normal,
							key === "css" ? updated : prev.css
						);
						return { ...prev, [key]: updated, aggregates };
					});
				}
				await loadAll();
			} else if (confirmState.type === "delete") {
				const row = confirmState.payload;
				const res = await fetch(`${apiBase}/submissions/${row._id}`, {
					method: "DELETE",
					headers: { Authorization: `Bearer ${token}` }
				});
				if (!res.ok) throw new Error("Delete failed");
				setData((prev) => {
					if (!prev) return prev;
					const key = row.formKey === "registration-26" ? "normal" : "css";
					const updatedList = prev[key].filter((r) => r._id !== row._id);
					const aggregates = recalcAggregates(
						key === "normal" ? updatedList : prev.normal,
						key === "css" ? updatedList : prev.css
					);
					return { ...prev, [key]: updatedList, aggregates };
				});
			}
			setConfirmState(null);
			setEditing(null);
			if (showAudit) loadAudit();
		} catch (e) {
			alert(e.message || "Operation failed");
		}
	}

	async function loadAudit() {
		setAudit((a) => ({ ...a, loading: true, error: null }));
		try {
			const res = await fetch(`${apiBase}/audit`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			const json = await res.json();
			if (!res.ok) throw new Error(json.error || "Failed to load audit");
			setAudit({ loading: false, items: json.items, error: null });
		} catch (e) {
			setAudit({ loading: false, items: [], error: e.message });
		}
	}
	useEffect(() => {
		if (showAudit) loadAudit();
	}, [showAudit]);

	return (
		<div className="registrations-page page-container">
			<div className="top-actions-wrapper">
				<div className="top-actions-search">
					<input
						type="text"
						placeholder="Search name..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="search-input"
					/>
					{search && (
						<button onClick={() => setSearch("")} className="clear-button">
							Clear
						</button>
					)}
				</div>
				<div className="top-actions-buttons">
					<button onClick={() => loadAll()} className="refresh-button" disabled={loading}>
						Refresh
					</button>
					<button onClick={() => setShowAudit((v) => !v)} className="history-button">
						{showAudit ? "Hide History" : "View History"}
					</button>
				</div>
			</div>

			{loading && <p>Loading...</p>}
			{error && <p className="status-error">{error}</p>}

			{data && !loading && (
				<>
					{/* Capacity / role stats */}
					{(() => {
						if (!data) return null;
						const levels = [
							{
								key: "Open",
								label: "Footprints (Open)",
								lindyRegex: /Lindy Hop\s+Footprints/i,
								jazzRegex: /Authentic Jazz\s+Footprints/i
							},
							{
								key: "Advanced",
								label: "Milestones (Advanced)",
								lindyRegex: /Lindy Hop\s+Milestones/i,
								jazzRegex: /Authentic Jazz\s+Milestones/i
							},
							{
								key: "Invitational",
								label: "Giant Steps (Invitational)",
								lindyRegex: /Lindy Hop\s+Giant Steps/i,
								jazzRegex: /Authentic Jazz\s+Giant Steps/i
							}
						];
						const stats = {};
						levels.forEach((l) => (stats[l.key] = { leaders: 0, followers: 0, jazz: 0 }));
						const getRole = (answers, levelKey) => {
							const target = `role_${levelKey}`.toLowerCase();
							const key = Object.keys(answers || {}).find((name) => name.toLowerCase() === target);
							return String(key ? answers[key] : "").trim().toLowerCase();
						};
						[...(data.normal || []), ...(data.css || [])].forEach((row) => {
							levels.forEach((lvl) => {
								const roleVal = getRole(row.answers, lvl.key);
								if (roleVal === "leader") stats[lvl.key].leaders += 1;
								else if (roleVal === "follower") stats[lvl.key].followers += 1;
							});
						});
						const agg = data.aggregates || {};
						return (
							<div className="tables-row">
								<div className="rs-capacity-group">
									<div className="panel">
										<strong className="panel-title">
											Lindy Hop Capacity
										</strong>
										<table>
											<thead>
												<tr>
													<th className="panel-header-cell">Level</th>
													<th className="panel-header-cell">Leads</th>
													<th className="panel-header-cell">Follows</th>
													<th className="panel-header-cell">Total</th>
												</tr>
											</thead>
											<tbody>
												{levels.map((lvl) => (
													<tr key={lvl.key}>
														<td className="panel-body-cell">{lvl.label}</td>
														<td className="panel-body-cell">{stats[lvl.key].leaders}</td>
														<td className="panel-body-cell">{stats[lvl.key].followers}</td>
														<td className="panel-body-cell">
															{stats[lvl.key].leaders + stats[lvl.key].followers}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
									<div className="panel">
										<strong className="panel-title">
											Authentic Jazz Capacity
										</strong>
										<table>
											<thead>
												<tr>
													<th className="panel-header-cell">Level</th>
													<th className="panel-header-cell">Registrations</th>
												</tr>
											</thead>
											<tbody>
												{levels.map((lvl) => (
													<tr key={lvl.key}>
														<td className="panel-body-cell">{lvl.label}</td>
														<td className="panel-body-cell">{stats[lvl.key].jazz}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
								{data.aggregates && (
									<div className="panel">
										<strong className=" panel-title">Money</strong>
										<table>
											<thead>
												<tr>
													<th className="panel-header-cell"></th>
													<th className="panel-header-cell">Total €</th>
													<th className="panel-header-cell">Paid €</th>
													<th className="panel-header-cell">Owed €</th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td className="panel-body-cell">Normal</td>
													<td className="panel-body-cell">{agg.totalNormal}</td>
													<td className="panel-body-cell">
														<span className="money-paid">{agg.paidNormal}</span>
													</td>
													<td className="panel-body-cell">
														<span className="money-owed">{agg.owedNormal}</span>
													</td>
												</tr>
												<tr>
													<td className="panel-body-cell">CSS</td>
													<td className="panel-body-cell">{agg.totalCss}</td>
													<td className="panel-body-cell">
														<span className="money-paid">{agg.paidCss}</span>
													</td>
													<td className="panel-body-cell">
														<span className="money-owed">{agg.owedCss}</span>
													</td>
												</tr>
												<tr>
													<td className="panel-body-cell">Grand</td>
													<td className="panel-body-cell">{agg.grandTotal}</td>
													<td className="panel-body-cell">
														<span className="money-paid">{agg.paidGrand}</span>
													</td>
													<td className="panel-body-cell">
														<span className="money-owed">{agg.owedGrand}</span>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								)}
								{/* T-shirt sizes panel - placed next to capacity ones */}
								<div className="rs-merch-group">
									<div className="panel totes-panel">
										<strong className="panel-title">Totes</strong>
										<table>
											<thead>
												<tr>
													<th className="panel-header-cell">Edition</th>
													<th className="panel-header-cell">Count</th>
												</tr>
											</thead>
											<tbody>
												{(() => {
													const counts = { 25: 0, 26: 0 };
													[data.normal || [], data.css || []].flat().forEach((row) => {
														[25, 26].forEach((edition) => {
															const value = getAnswerValue(row.answers, `tote_diy${edition}`);
															if (String(value || "").trim().toLowerCase() === "yes") counts[edition] += 1;
														});
													});
													return [25, 26].map((edition) => (
														<tr key={edition}>
															<td className="panel-body-cell">DIY 20{edition}</td>
															<td className="panel-body-cell">{counts[edition]}</td>
														</tr>
													));
												})()}
											</tbody>
										</table>
									</div>
									<div className="panel tshirts-panel">
										<strong className="panel-title">Kontogiorgis</strong>
										<table>
											<thead>
												<tr>
													<th className="panel-header-cell">Size</th>
													<th className="panel-header-cell">Count</th>
												</tr>
											</thead>
											<tbody>
												{(() => {
													// compute t-shirt counts from both datasets
													// canonical sizes we care about
													const CANONICAL = ["S", "M", "L", "XL", "XXL"];
													const counts = { S: 0, M: 0, L: 0, XL: 0, XXL: 0 };
													const isNoTshirt = (v) => {
														const s = String(v || "")
															.trim()
															.toLowerCase();
														return (
															!s ||
															/^(no|none|n\/a)$/.test(s) ||
															/^(no|none)\s*(t[- ]?shirt|shirt)$/.test(s)
														);
													};
													const normalizeSize = (raw) => {
														if (!raw) return null;
														const s = String(raw || "")
															.trim()
															.toLowerCase();
														if (/^(s|small)$/i.test(s)) return "S";
														if (/^(m|med|medium)$/i.test(s)) return "M";
														if (/^(l|large)$/i.test(s)) return "L";
														if (/^(xl|x[- ]?large|extra[- ]?large)$/i.test(s) || /^1x$/i.test(s))
															return "XL";
														if (/^(xxl|2x|2xl|xx[- ]?large)$/i.test(s) || /^2x$/i.test(s))
															return "XXL";
														return null; // other/unknown sizes are ignored
													};
													const add = (val) => {
														if (!val) return;
														let sizes = Array.isArray(val)
															? val
															: String(val)
																	.split(/[,|\\/]+/)
																	.map((s) => s.trim())
																	.filter(Boolean);
														sizes.forEach((raw) => {
															if (isNoTshirt(raw)) return;
															const k = normalizeSize(raw);
															if (k && CANONICAL.includes(k)) counts[k] = (counts[k] || 0) + 1;
														});
													};
													(data.normal || []).forEach((r) =>
														add(r.answers?.tshirt_konto || r.answers?.tshirt || r.answers?.tshirt_diy)
													);
													(data.css || []).forEach((r) =>
														add(r.answers?.tshirt_konto || r.answers?.tshirt || r.answers?.tshirt_diy)
													);
													const entries = CANONICAL; // show in S, M, L, XL, XXL order
													const totalCount = entries.reduce((s, k) => s + (counts[k] || 0), 0);
													if (totalCount === 0)
														return (
															<tr>
																<td className="panel-body-cell" colSpan={2}>
																	No t-shirt data
																</td>
															</tr>
														);
													return entries.map((sz) => (
														<tr key={sz}>
															<td className="panel-body-cell">{sz}</td>
															<td className="panel-body-cell">{counts[sz]}</td>
														</tr>
													));
												})()}
											</tbody>
										</table>
									</div>
									<div className="panel tshirts-panel">
										<strong className="panel-title">Kontogiorgaina</strong>
										<table>
											<thead>
												<tr>
													<th className="panel-header-cell">Size</th>
													<th className="panel-header-cell">Count</th>
												</tr>
											</thead>
											<tbody>
												{(() => {
													// compute t-shirt counts from both datasets
													// canonical sizes we care about
													const CANONICAL = ["S", "M", "L", "XL", "XXL"];
													const counts = { S: 0, M: 0, L: 0, XL: 0, XXL: 0 };
													const isNoTshirt = (v) => {
														const s = String(v || "")
															.trim()
															.toLowerCase();
														return (
															!s ||
															/^(no|none|n\/a)$/.test(s) ||
															/^(no|none)\s*(t[- ]?shirt|shirt)$/.test(s)
														);
													};
													const normalizeSize = (raw) => {
														if (!raw) return null;
														const s = String(raw || "")
															.trim()
															.toLowerCase();
														if (/^(s|small)$/i.test(s)) return "S";
														if (/^(m|med|medium)$/i.test(s)) return "M";
														if (/^(l|large)$/i.test(s)) return "L";
														if (/^(xl|x[- ]?large|extra[- ]?large)$/i.test(s) || /^1x$/i.test(s))
															return "XL";
														if (/^(xxl|2x|2xl|xx[- ]?large)$/i.test(s) || /^2x$/i.test(s))
															return "XXL";
														return null; // other/unknown sizes are ignored
													};
													const add = (val) => {
														if (!val) return;
														let sizes = Array.isArray(val)
															? val
															: String(val)
																	.split(/[,|\\/]+/)
																	.map((s) => s.trim())
																	.filter(Boolean);
														sizes.forEach((raw) => {
															if (isNoTshirt(raw)) return;
															const k = normalizeSize(raw);
															if (k && CANONICAL.includes(k)) counts[k] = (counts[k] || 0) + 1;
														});
													};
													(data.normal || []).forEach((r) =>
														add(r.answers?.tshirt_konto_f)
													);
													(data.css || []).forEach((r) =>
														add(r.answers?.tshirt_konto_f)
													);
													const entries = CANONICAL; // show in S, M, L, XL, XXL order
													const totalCount = entries.reduce((s, k) => s + (counts[k] || 0), 0);
													if (totalCount === 0)
														return (
															<tr>
																<td className="panel-body-cell" colSpan={2}>
																	No t-shirt data
																</td>
															</tr>
														);
													return entries.map((sz) => (
														<tr key={sz}>
															<td className="panel-body-cell">{sz}</td>
															<td className="panel-body-cell">{counts[sz]}</td>
														</tr>
													));
												})()}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						);
					})()}

					<div className="export-section">
						<button
							className="export-button"
							onClick={() => exportDatasetAsCsv(filteredNormal, "normal")}
						>
							Export Normal
						</button>
					</div>
					<Table
						title="Normal Form"
						data={filteredNormal}
						filtering={!!search.trim()}
						onEdit={onEdit}
						onDelete={onDelete}
						onTogglePaid={onTogglePaid}
						onToggleReg={onToggleReg}
						editing={editing}
						setEditing={setEditing}
						setConfirmState={setConfirmState}
						confirmEdit={confirmEdit}
						schemaMap={schemaMap}
						orderMap={CUSTOM_ORDER_MAP}
						onEditNote={onEditNote}
					/>
					<div className="export-section">
						<button className="export-button" onClick={() => exportDatasetAsCsv(filteredCss, "css")}>
							Export CSS
						</button>
					</div>
					<Table
						title="CSS Form"
						className="registrations-table--second"
						data={filteredCss}
						filtering={!!search.trim()}
						onEdit={onEdit}
						onDelete={onDelete}
						onTogglePaid={onTogglePaid}
						onToggleReg={onToggleReg}
						editing={editing}
						setEditing={setEditing}
						setConfirmState={setConfirmState}
						confirmEdit={confirmEdit}
						schemaMap={schemaMap}
						orderMap={CUSTOM_ORDER_MAP}
						onEditNote={onEditNote}
					/>
				</>
			)}

			{noteEditing && (
				<div
					className="rs-modal-overlay"
					onClick={() => {
						setNoteEditing(null);
						setConfirmDeleteNote(false);
					}}
				>
					<div className="rs-modal-box" onClick={(e) => e.stopPropagation()}>
						<h4 className="rs-modal-title">
							{confirmDeleteNote
								? "Confirm Note Deletion"
								: `Note for ${noteEditing.row.answers?.name || "Entry"}`}
						</h4>
						{confirmDeleteNote ? (
							<>
								<p className="rs-paragraph-small">
									This will permanently delete this note. Continue?
								</p>
								<div className="rs-modal-actions">
									<button
										onClick={() => {
											setConfirmDeleteNote(false);
											deleteNote();
										}}
										className="yes-button"
									>
										Yes
									</button>
									<button
										onClick={() => setConfirmDeleteNote(false)}
										className="no-button"
									>
										No
									</button>
								</div>
							</>
						) : (
							<>
								<div className="rs-modal-body" style={{ marginTop: "1rem", marginBottom: "1.5rem" }}>
									<textarea
										rows={6}
										className="rs-textarea"
										placeholder="Enter admin notes here..."
										value={noteEditing.text}
										onChange={(e) => setNoteEditing({ ...noteEditing, text: e.target.value })}
									/>
								</div>
								<div className="rs-modal-actions" style={{ justifyContent: "space-between" }}>
									{noteEditing.row.adminNote ? (
										<button
											onClick={() => setConfirmDeleteNote(true)}
											className="delete-button"
										>
											Delete
										</button>
									) : (
										<div />
									)}
									<div className="right-buttons">
										<button
											onClick={() => saveNote()}
											className="save-button"
										>
											Save
										</button>
										<button
											onClick={() => setNoteEditing(null)}
											className="cancel-button"
										>
											Cancel
										</button>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			)}

			{confirmState && (confirmState.type !== "edit" || editing) && (
				<div className="rs-modal-overlay">
					<div className="rs-modal-box">
						<h4 className="rs-modal-title">
							Confirm {confirmState.type === "edit" ? "Changes" : "Deletion"}
						</h4>
						{confirmState.type === "edit" ? (
							<div className="rs-modal-body">
								<table className="rs-modal-table">
									<thead>
										<tr>
											<th className="rs-th-mini">Field</th>
											<th className="rs-th-mini">Previous</th>
											<th className="rs-th-mini">New</th>
										</tr>
									</thead>
									<tbody>
										{Object.entries(confirmState.payload.changes).map(([k, v]) => (
											<tr key={k}>
												<td className="rs-td-mini">{k}</td>
												<td className="rs-td-mini">{String(editing.original[k] ?? "")}</td>
												<td className="rs-td-mini">
													{Array.isArray(v) ? v.join(", ") : String(v)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<p className="rs-paragraph-small">
								This will permanently delete the registration. Continue?
							</p>
						)}
						<div className="rs-modal-actions">
							<button onClick={() => performConfirmed()} className=" yes-button">
								Yes
							</button>
							<button onClick={() => setConfirmState(null)} className="no-button">
								No
							</button>
						</div>
					</div>
				</div>
			)}

			{showAudit && (
				<div className="rs-audit-overlay" onClick={() => setShowAudit(false)}>
					<div className="rs-audit-panel" onClick={(e) => e.stopPropagation()}>
						<div className="rs-audit-header">
							<h4 className="rs-modal-title">Change History</h4>
							<button
								type="button"
								className="x-button"
								aria-label="Close change history"
								onPointerDown={(event) => {
									event.preventDefault();
									event.stopPropagation();
									setShowAudit(false);
								}}
								onClick={(event) => event.stopPropagation()}
							>
								×
							</button>
						</div>
						{audit.loading && <p className="rs-paragraph-small">Loading...</p>}
						{audit.error && <p className="status-error">{audit.error}</p>}
						{!audit.loading && !audit.error && (
							<div className="rs-audit-list">
								{audit.items.length === 0 && (
									<p className="rs-paragraph-small">No audit entries found.</p>
								)}
								<ul>
									{audit.items.map((entry) => {
										if (entry.field === "tshirt_diy" || entry.field === "tote_diy") return null; // hide legacy DIY merch
										const displayField = entry.field === "computedTotal" ? "total" : entry.field;
										const displayMessage = entry.message
											? String(entry.message).replace(/computedTotal/g, "total")
											: entry.message;
										// Try to locate the submission locally so we can show the registrant name
										const allSubs = [...(data?.normal || []), ...(data?.css || [])];
										const submission = allSubs.find(
											(s) => String(s._id) === String(entry.submissionId)
										);
										const formName =
											submission?.answers?.name || (entry.prev && entry.prev.name) || "(record)";
										return (
											<li key={entry._id} className="rs-audit-item">
												<span className="rs-muted">{formatAuditDate(entry.createdAt)} –</span>
												<strong>{entry.adminUser || "?"}</strong> - {entry.action}{" "}
												{displayField || "(record)"} - <strong>{formName}</strong>
											</li>
										);
									})}
								</ul>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
